/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { FeatureTag } from '@g17eco/core';
import { DisplayOption, DownloadUtrvStatusCombined, OnboardingStep } from './common';
import { SurveyOverviewMode } from './surveyOverview';
import { ProductCodes } from './products';

export enum CompanyAgreement {
  CompanyTrackerServicesAgreement = 'CompanyTrackerServicesAgreement'
}

export enum DescriptionType {
  ExternalLink = 'external_link',
  HyGraph = 'hygraph',
  Text = 'text',
}


interface GraphCms {
  type: DescriptionType.HyGraph,
  code: string
  text: string;
}

interface ExternalLink {
  type: DescriptionType.ExternalLink,
  text: string;
  url: string
}

interface TextNode {
  type: DescriptionType.Text,
  text: string
}

export type DescriptionElement = TextNode | ExternalLink | GraphCms;

/** Represent Agreement modal data **/
interface AgreementData {
  title: string;
  description: DescriptionElement[];
}



export interface AgreementConfig<T extends string = string> {
  code: T;

  /** These apply for initiative created Date **/
  fromDate?: Date;

  /** These only take active from specific date, therefore they can be scheduled **/
  startDate?: Date;

  /**
   * It will no longer be required or active after end date
   * Most of the time it should be replaced by newer version of agreement
   */
  endDate?: Date;

  /**
   * Custom rendering data
   * Mostly to populate custom agreement modal, not the actual agreement that
   * ideally will live outside the platform.
   */
  data?: AgreementData
}

export interface CustomAgreementConfig extends AgreementConfig {
  type?: 'company' | 'sponsorship';
}

export enum AppCode {
  WWG = 'wwg',
  TOLI = 'toli',
  SGXESGenome = 'sgx_esgenome',
  CompanyTrackerStarter = 'company_tracker_starter',
  CompanyTracker = 'company_tracker_light',
  CompanyTrackerPro = 'company_tracker_pro',
  CompanyTrackerEnterprise = 'company_tracker_enterprise',
  PortfolioTrackerExchange = 'portfolio_tracker_exchange',
  PortfolioTrackerExchangeSGX = 'portfolio_tracker_exchange_sgx',
  MaterialityTracker = 'materiality_assessment',
  AseanBundleGPT = 'asean_bundle_gpt',
  AseanBundleUnravel = 'asean_bundle_unravel',
  EmissionTracker = 'emission_tracker',
}

export interface AppConfig {
  code: AppCode;
  productCode: ProductCodes;
  validProductCodes: ProductCodes[];

  // App Setup
  name: string;
  logo: string;
  rootAppPath: string;
  onboardingPath: string;

  /** Overall website change **/
  whitelabel?: {
    logo?: string;
  };
  /** App specific changes **/
  settings: {
    defaultSurveyOverviewMode?: SurveyOverviewMode;

    /**
     * Used by question progress circles to determine what to display or "add" to scope
     * Note: used to be only used when company is CTL, now is respected.
     */
    overviewRecommendedAddons: string[];

    /**
     * Determine what appears at the top of scope view (core packs)
     * Also used the preferred alternative codes for SDG Contributions
     * detail view, question list and insight references etc.
     * The order is important
     **/
    settingsRecommendedAddons: string[];

    /**
     * All of these codes will be split to free and premium groups and displayed
     * as option to add to the scope
     */
    availableAddons?: string[];
    requiresTermsAndConditions?: boolean;
    onboardingSteps?: OnboardingStep[];
    companyAgreementsRequired?: AgreementConfig<CompanyAgreement>[];
    canViewAllPacks?: boolean;
    canEditInsightOverview?: boolean;
    canShowTargetsAndTrends?: boolean;
    canShowSDGDetails?: boolean;
    defaultDownloadOptions?: {
      metricStatuses?: DownloadUtrvStatusCombined[];
      metricOverrides?: DisplayOption[];
    };
    betaFeatures?: FeatureTag[];
  };
}

export enum ProductAppType {
  MaterialityTracker = 'materiality-tracker',
  CompanyTracker = 'company-tracker',
  SGXESGenome = 'sgx-esgenome',
  PortfolioTracker = 'portfolio-tracker',
  AssuranceTracker = 'assurance-tracker',
  SDGAP = 'sdgap',
  WorldTracker = 'world-tracker',
  EmissionsCalculator = 'emissions-calculator',
  EmissionsTracker = 'emissions-tracker',
}
